import { AxiosManager } from '../../../services/axiosManager';
import BaseApi from '../../../services/common/base-api';
import { toStatsError } from '../../../utils/statistics/errors';

/** `format=v2`: the usage route answers with `StatsUsage` from the statistics services. */
export const USAGE_FORMAT = 'v2';

class BillingService extends BaseApi {
  constructor() {
    super(process.env.REACT_APP_ADMIN_API_URL, 'api/web/client');
  }

  _get(url) {
    return this.axios
      .get(url)
      .then((res) => res.data.data)
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }

  _post(url, data) {
    return this.axios
      .post(url, data)
      .then((res) => res.data.data)
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }

  _delete(url, data) {
    return this.axios
      .delete(url, { data })
      .then((res) => res.data.data)
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }

  getBillingProfile() {
    return this._get('/billing-profiles');
  }

  createBillingProfile(data) {
    return this._post('/billing-profiles', data);
  }

  getPaymentCard() {
    return this._get('/payment-card');
  }

  getPaymentCardUpdateLink() {
    return this._post('/payment-card/update-link', {});
  }

  getCurrentSubscription() {
    // `time` (server clock) lives at the response root, not inside `data`
    return this.axios
      .get('/subscriptions/current')
      .then((res) => ({ subscription: res.data.data, serverTime: res.data.time }))
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }

  createSubscription(data) {
    return this._post('/subscriptions', data);
  }

  upgradeSubscription(data) {
    return this._post('/subscriptions/upgrade', data);
  }

  getTariffs() {
    return this._get('/tariffs');
  }

  deposit(data) {
    return this._post('/subscriptions/deposit', data);
  }

  downgradeSubscription(data) {
    return this._post('/subscriptions/downgrade', data);
  }

  cancelSubscription(data) {
    return this._post('/subscriptions/cancel', data);
  }

  renewSubscription(data) {
    return this._delete('/subscriptions/cancel', data);
  }

  payInvoice(invoiceId) {
    return this._post(`/invoices/${invoiceId}/pay`, {});
  }

  getInvoices({ offset = 0, limit = 5 } = {}) {
    return this._get(`/invoices?offset=${offset}&limit=${limit}`);
  }

  getInvoicesPage({ offset = 0, limit = 10 } = {}) {
    return this._get(`/invoices?offset=${offset}&limit=${limit}`);
  }

  /**
   * `StatsUsage` for local days `startDate`..`endDate` (inclusive, `YYYY-MM-DD`)
   * in `tz`, for the card's own `mode` (LIVE/TEST), not the session's.
   * Rejects with a structured `{ status, message, field }` error.
   */
  getUsage({ startDate, endDate, mode, tz }) {
    return this.axios
      .get('/billing/usage', { params: { startDate, endDate, mode, format: USAGE_FORMAT, tz } })
      .then((res) => res.data.data)
      .catch((err) => {
        throw toStatsError(err);
      });
  }

  getPeriodStatements({ offset = 0, limit = 10 } = {}) {
    return this.axios
      .get('/billing/period-statements', { params: { offset, limit } })
      .then((res) => res.data.data)
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }

  requestCustomPlan(data) {
    return this.axios
      .post('/subscriptions/request-custom', data)
      .then((res) => res.data)
      .catch((err) => {
        throw AxiosManager.stringifyError(err);
      });
  }
}

export default new BillingService();
